import React, { useEffect, useState } from "react";
import { Card, Empty, Field } from "../../dashboard/primitives";
import { useAuth } from "../../auth/AuthContext";
import {
  ACCOUNT_ROLES,
  addMember,
  listMembers,
  removeMember,
  setMemberPassword,
  updateMemberRole,
} from "../../lib/accounts";

const errText = (e) => e?.response?.data?.error || e?.message || "Something went wrong";

// A readable temp password the owner can share; the teammate changes it later.
const genPassword = () =>
  "bboy-" + Math.random().toString(36).slice(2, 8) + Math.floor(10 + Math.random() * 89);

const ROLE_BLURB = {
  owner: "Manages the team & billing",
  editor: "Can message, approve, and log bugs",
  viewer: "Read-only access",
};

const TeamSection = () => {
  const { user, account, isAccountOwner } = useAuth();
  const accountId = account?.id;
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  // invite form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("editor");
  const [password, setPassword] = useState("");
  const [inviting, setInviting] = useState(false);

  const refresh = async () => {
    if (!accountId) return;
    try {
      setMembers(await listMembers(accountId));
    } catch (err) {
      setError(errText(err));
    }
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  if (!accountId) {
    return (
      <Card title="Team">
        <Empty>You're not part of an account yet.</Empty>
      </Card>
    );
  }

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setNote(null);
    try {
      const res = await addMember(accountId, { email, displayName: name, role, password });
      setNote(
        res.passwordSet
          ? `${email} can sign in now with the temp password: ${password}. Share it with them; they can change it later.`
          : res.created
          ? "Login created — set a temp password for them with “Set password”."
          : "Existing login added to your team."
      );
      setEmail("");
      setName("");
      setRole("editor");
      setPassword("");
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (uid, nextRole) => {
    setBusyUid(uid);
    setError(null);
    try {
      await updateMemberRole(accountId, uid, nextRole);
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  const onRemove = async (uid, label) => {
    if (!window.confirm(`Remove ${label} from the team?`)) return;
    setBusyUid(uid);
    setError(null);
    try {
      await removeMember(accountId, uid);
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  const onSetPassword = async (uid, label) => {
    const pw = window.prompt(`Set a new temporary password for ${label} (min 6 characters).\nShare it with them; they can change it after signing in.`);
    if (pw === null) return; // cancelled
    if (pw.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setBusyUid(uid);
    setError(null);
    setNote(null);
    try {
      await setMemberPassword(accountId, uid, pw);
      setNote(`New temp password set for ${label}: ${pw}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  return (
    <Card title={`Team · ${account.name}`}>
      {error && <div style={{ color: "var(--fw-red)", fontSize: 12, fontFamily: "var(--fw-mono)", marginBottom: 8 }}>{error}</div>}
      {note && <div style={{ color: "var(--fw-muted)", fontSize: 12, marginBottom: 8 }}>{note}</div>}

      {members === null ? (
        <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>Loading team…</div>
      ) : members.length === 0 ? (
        <Empty>No teammates yet</Empty>
      ) : (
        members.map((m) => {
          const busy = busyUid === m.uid;
          const isMe = m.uid === user?.uid;
          return (
            <div
              key={m.uid}
              style={{
                display: "grid",
                gridTemplateColumns: isAccountOwner ? "2fr 1fr auto auto" : "2fr 1fr",
                gap: 12,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid var(--fw-line)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {m.displayName || "—"}
                  {isMe && (
                    <span style={{ fontFamily: "var(--fw-mono)", fontSize: 10, color: "var(--fw-red)", marginLeft: 8 }}>YOU</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>{m.email}</div>
              </div>

              {isAccountOwner && !isMe ? (
                <select
                  className="fw-select"
                  style={{ padding: "6px 8px", fontSize: 12 }}
                  value={m.role || "editor"}
                  disabled={busy}
                  onChange={(e) => changeRole(m.uid, e.target.value)}
                >
                  {ACCOUNT_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>
                  {m.role} · {ROLE_BLURB[m.role] || ""}
                </div>
              )}

              {isAccountOwner && !isMe && (
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", whiteSpace: "nowrap" }} disabled={busy} onClick={() => onSetPassword(m.uid, m.displayName || m.email)}>
                  Set password
                </button>
              )}
              {isAccountOwner && !isMe && (
                <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "#c0392b" }} disabled={busy} onClick={() => onRemove(m.uid, m.displayName || m.email)}>
                  Remove
                </button>
              )}
            </div>
          );
        })
      )}

      {isAccountOwner ? (
        <form onSubmit={invite} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 1.4fr auto", gap: 8, alignItems: "end", marginTop: 16 }}>
          <Field label="Invite teammate — email">
            <input className="fw-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" required />
          </Field>
          <Field label="Name">
            <input className="fw-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Role">
            <select className="fw-select" value={role} onChange={(e) => setRole(e.target.value)}>
              {ACCOUNT_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
          <Field label="Temp password">
            <div style={{ display: "flex", gap: 4 }}>
              <input className="fw-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6" minLength={6} required style={{ flex: 1, minWidth: 0 }} />
              <button type="button" className="fw-btn fw-btn--ghost" style={{ padding: "6px 8px", whiteSpace: "nowrap" }} onClick={() => setPassword(genPassword())}>
                Gen
              </button>
            </div>
          </Field>
          <button type="submit" className="fw-btn fw-btn--primary" style={{ height: 38 }} disabled={inviting}>
            {inviting ? "Inviting…" : "Invite"}
          </button>
        </form>
      ) : (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--fw-muted)" }}>
          Only an account owner can invite or manage teammates.
        </div>
      )}
    </Card>
  );
};

export default TeamSection;
